import React, { useState, useMemo, useEffect } from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import { FiHome as Home, FiDollarSign as DollarSign, FiTag as Tag, FiShield as Shield, FiAlertTriangle as AlertTriangle, FiZap as Zap, FiServer as Server, FiCpu as Cpu, FiHelpCircle as HelpCircle, FiInfo as Info } from 'react-icons/fi';
import CloudOpsGettingStartedBanner from './CloudOpsGettingStartedBanner';
import { useCloudOpsOnboardingStatus } from '../../../hooks/useCloudOpsOnboardingStatus';
import { demoModeManager } from '../../../services/demoModeManager';
import HelpPanel from '../../../components/common/HelpPanel';
import { CloudOpsHelp } from '../../../features/cloudops/help';

type CloudOpsPage = 'overview' | 'finops' | 'anomalies' | 'tagging' | 'guardrails' | 'recommendations' | 'compute' | 'endpoints';

interface NavItem {
  path: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}

const CloudOpsLayout = () => {
  const location = useLocation();
  const { isComplete: onboardingComplete, refresh: refreshOnboarding } = useCloudOpsOnboardingStatus();
  const [showOnboarding, setShowOnboarding] = useState(() => {
    return localStorage.getItem('cloudops_onboarding_dismissed') !== 'true';
  });
  const [showHelpPanel, setShowHelpPanel] = useState(false);

  // Refresh onboarding status on route change to pick up newly completed steps
  useEffect(() => {
    refreshOnboarding();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Auto-dismiss when all onboarding steps are complete
  useEffect(() => {
    if (onboardingComplete) {
      localStorage.setItem('cloudops_onboarding_dismissed', 'true');
      setShowOnboarding(false);
    }
  }, [onboardingComplete]);

  const handleDismissOnboarding = () => {
    localStorage.setItem('cloudops_onboarding_dismissed', 'true');
    setShowOnboarding(false);
  };

  const handleShowOnboarding = () => {
    localStorage.removeItem('cloudops_onboarding_dismissed');
    setShowOnboarding(true);
  };

  const cloudOpsNavItems: NavItem[] = [
    { path: '/cloudops/overview', label: 'Overview', icon: Home },
    { path: '/cloudops/finops', label: 'FinOps', icon: DollarSign },
    { path: '/cloudops/anomalies', label: 'Anomalies', icon: AlertTriangle },
    { path: '/cloudops/tagging', label: 'Tagging', icon: Tag },
    { path: '/cloudops/guardrails', label: 'Guardrails', icon: Shield },
    { path: '/cloudops/cost-recommendations', label: 'Cost Recommendations', icon: Zap },
    { path: '/cloudops/compute', label: 'Compute', icon: Cpu },
    { path: '/cloudops/endpoints', label: 'Monitoring', icon: Server },
  ];

  const isActive = (path: string): boolean => {
    if (path === '/cloudops/overview' && location.pathname === '/cloudops') {
      return true;
    }
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  // Determine active page for context-aware help
  const getActivePage = (): CloudOpsPage => {
    const path = location.pathname;
    if (path.includes('/cloudops/finops')) return 'finops';
    if (path.includes('/cloudops/anomalies')) return 'anomalies';
    if (path.includes('/cloudops/tagging')) return 'tagging';
    if (path.includes('/cloudops/guardrails')) return 'guardrails';
    if (path.includes('/cloudops/cost-recommendations')) return 'recommendations';
    if (path.includes('/cloudops/compute')) return 'compute';
    if (path.includes('/cloudops/endpoints')) return 'endpoints';
    return 'overview';
  };

  // Dynamic help panel title
  const helpPanelTitle = useMemo(() => {
    const activePage = getActivePage();
    const titles: Record<CloudOpsPage, string> = {
      overview: 'Overview Help',
      finops: 'FinOps Help',
      anomalies: 'Anomalies Help',
      tagging: 'Tagging Help',
      guardrails: 'Guardrails Help',
      recommendations: 'Cost Recommendations Help',
      compute: 'Compute Help',
      endpoints: 'Endpoints Help',
    };
    return titles[activePage] || 'Help';
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return (
    <div>
      {/* CloudOps Sub-Navigation */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 transition-colors">
        <div className="mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <nav className="flex space-x-8" aria-label="CloudOps navigation">
              {cloudOpsNavItems.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`${
                      isActive(item.path)
                        ? 'border-blue-500 text-gray-900 dark:text-white'
                        : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
                    } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm inline-flex items-center transition-colors`}
                  >
                    <Icon className="w-4 h-4 mr-2" />
                    {item.label}
                  </Link>
                );
              })}
            </nav>
            {/* Action buttons */}
            <div className="flex items-center gap-3">
              {/* Getting Started banner toggle - hidden when all steps complete */}
              {!showOnboarding && !onboardingComplete && (
                <button
                  onClick={handleShowOnboarding}
                  className="p-2 text-gray-500 dark:text-gray-400 hover:text-emerald-600 dark:hover:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 rounded-lg transition-colors"
                  title="Getting Started"
                  aria-label="Getting Started"
                >
                  <Info className="w-5 h-5" />
                </button>
              )}
              {/* Help panel toggle */}
              <button
                onClick={() => setShowHelpPanel(true)}
                className="p-2 text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                title="Help"
                aria-label="Help"
              >
                <HelpCircle className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Onboarding Banner - hidden in demo mode */}
      {showOnboarding && !demoModeManager.getDemoMode().isDemoMode && (
        <div className="mx-auto px-4 sm:px-6 lg:px-8 pt-6">
          <CloudOpsGettingStartedBanner onDismiss={handleDismissOnboarding} />
        </div>
      )}

      {/* CloudOps Content */}
      <Outlet />

      {/* Help Panel */}
      <HelpPanel
        isOpen={showHelpPanel}
        onClose={() => setShowHelpPanel(false)}
        title={helpPanelTitle}
      >
        <CloudOpsHelp activePage={getActivePage()} />
      </HelpPanel>
    </div>
  );
};

export default CloudOpsLayout;
